import React, {
  type ChangeEvent,
  type Dispatch,
  memo,
  type ReactNode,
  type SetStateAction,
  type SyntheticEvent,
  useCallback,
  useState,
} from "react";
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Box,
  Divider,
  Grid,
  Icon,
  Link,
  TextField,
  Typography,
} from "@mui/material";
import CalendarTodayOutlinedIcon from "@mui/icons-material/CalendarTodayOutlined";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";

import JoinCampaignLocationHeader from "@/features/campaigns/components/joinCampaignCommon/JoinCampaignLocationHeader";
import RpSvgIcon from "@/components/icons/RpSvgIcon";
import RpCurrencyFieldBase from "@/components/muiCustom/textField/RpCurrencyField";
import RpMuiSwitch from "@/components/muiCustom/switch/RpMuiSwitch";
import RpNumberFieldBase from "@/components/muiCustom/textField/RpNumberField";
import Feedback from "@/shared/components/feedback/Feedback";
import {
  calculateNationalPlusUpCosts,
  formatNationalPlusUpLocationDisplayName,
  formatNationalMailingWeek,
  isNationalPlusUpLocationJoinable,
  validateNationalPlusUpStoreCopyQuantity,
} from "../utils/nationalPlusUpHelpers";
import NationalPlusUpToolTips from "./NationalPlusUpToolTips";
import {
  getRRDStoreCopyDefaultQuantity,
  isRRDStoreCopyFixedQuantity,
  resolveRRDStoreCopyConfig,
} from "@/features/campaigns/direct-mail/rrd/utils/rrdStoreCopyConfigHelper";
import {
  isTemporarilyClosedLocation,
  TemporaryClosedStatusNote,
} from "@/components/muiCustom/note/InlineStatusNote";

type NationalPlusUpRecord = Record<string, any>;

type NationalPlusUpLocationAccordionProps = {
  loc: NationalPlusUpRecord;
  value?: NationalPlusUpRecord;
  shouldExpandIt?: boolean;
  setExpanded: Dispatch<SetStateAction<Array<string | number>>>;
  setFieldValue: (_field: string, _value: unknown) => void;
};

const RpNumberField = RpNumberFieldBase as React.ComponentType<
  Record<string, any>
>;
const RpCurrencyField = RpCurrencyFieldBase as React.ComponentType<
  Record<string, any>
>;

function NationalPlusUpLocationAccordion({
  loc,
  value = {},
  shouldExpandIt,
  setExpanded,
  setFieldValue,
}: NationalPlusUpLocationAccordionProps) {
  const locationId = loc?.id;
  const storeCopyConfig = resolveRRDStoreCopyConfig({ location: value });
  const isFixedStoreCopyQuantity = isRRDStoreCopyFixedQuantity(storeCopyConfig);
  const fixedStoreCopyQuantity =
    getRRDStoreCopyDefaultQuantity(storeCopyConfig);
  const storeCopyError = validateNationalPlusUpStoreCopyQuantity(
    value?.store_copy_qnt,
    value?.store_copy,
    storeCopyConfig
  );
  const [feedbackOpen, setFeedbackOpen] = useState(false);
  const handleOpenFeedback = useCallback(() => setFeedbackOpen(true), []);
  const handleCloseFeedback = useCallback(() => setFeedbackOpen(false), []);
  const locationName = formatNationalPlusUpLocationDisplayName(value);
  const hasDistributionSection = Boolean(value?.distribution_available);

  const updateCosts = (nextValues: NationalPlusUpRecord) => {
    const costs = calculateNationalPlusUpCosts({
      distribution: nextValues.distribution,
      plusUpCost: value.plus_up_cost_admin,
      storeCopy: nextValues.store_copy,
      storeCopyQnt: nextValues.store_copy_qnt,
      storeCopyCpm: value.store_copy_cpm,
      storeCopyConfig,
    });
    setFieldValue(`${locationId}.plus_up_cost`, costs.plus_up_cost);
    setFieldValue(`${locationId}.store_copy_cost`, costs.store_copy_cost);
    setFieldValue(`${locationId}.total_cost`, costs.total_cost);
  };

  const handleDistributionChange = (event: ChangeEvent<HTMLInputElement>) => {
    const distribution = event.target.checked;
    setFieldValue(`${locationId}.distribution`, distribution);
    updateCosts({ ...value, distribution });
  };

  const handleStoreCopyChange = (event: ChangeEvent<HTMLInputElement>) => {
    const store_copy = event.target.checked;
    const store_copy_qnt = store_copy
      ? fixedStoreCopyQuantity || value.store_copy_qnt || ""
      : "";
    setFieldValue(`${locationId}.store_copy`, store_copy);
    setFieldValue(`${locationId}.store_copy_qnt`, store_copy_qnt);
    updateCosts({ ...value, store_copy, store_copy_qnt });
  };

  const handleStoreCopyQntChange = (event: ChangeEvent<HTMLInputElement>) => {
    if (isFixedStoreCopyQuantity) {
      return;
    }
    const rawValue = event.target.value;
    const store_copy_qnt =
      rawValue && !Number.isNaN(Number(rawValue)) ? parseInt(rawValue, 10) : "";
    setFieldValue(`${locationId}.store_copy_qnt`, store_copy_qnt);
    updateCosts({ ...value, store_copy_qnt });
  };

  const handleExpand =
    (panel: string | number) =>
    (_event: SyntheticEvent, isExpanded: boolean) => {
      if (isExpanded) {
        setExpanded((current) => [...new Set([...current, panel])]);
      } else {
        setExpanded((current) => current.filter((id) => id !== panel));
      }
    };

  return (
    <Accordion
      expanded={shouldExpandIt}
      onChange={handleExpand(locationId)}
      sx={{
        background: "var(--neutral-white) 0% 0% no-repeat padding-box",
        boxShadow: "none",
        border: "1px solid var(--neutral-200)",
        borderRadius: "var(--radius-container)",
        marginBottom: "0.5rem!important",
        width: "100%",
        "&:before": { display: "none" },
      }}
      slotProps={{ transition: { unmountOnExit: true } }}
    >
      <AccordionSummary
        expandIcon={<ExpandMoreIcon />}
        className="location-header"
        sx={{
          "& .MuiAccordionSummary-content": {
            padding: "0 !important",
            margin: "0 !important",
          },
          ":not(.Mui-expanded):hover": {
            background: "var(--neutral-200)",
            borderRadius: "var(--radius-container)",
          },
        }}
      >
        <JoinCampaignLocationHeader
          locationName={locationName}
          isFullyFilled={isNationalPlusUpLocationJoinable(value)}
          locationStatusNote={
            isTemporarilyClosedLocation(loc) ? (
              <TemporaryClosedStatusNote location={loc} />
            ) : null
          }
        />
      </AccordionSummary>
      <AccordionDetails
        className="location-details"
        sx={{ px: { xs: 2, md: 5 }, pb: 3 }}
      >
        <Divider sx={{ mb: 3 }} />
        <Grid container spacing={3}>
          {hasDistributionSection ? (
            <>
              <Grid size={12}>
                <Grid container spacing={3} alignItems="flex-start">
                  <Grid size={{ xs: 12, md: 7.5 }}>
                    <Grid container spacing={3} alignItems="flex-start">
                      <Grid size={{ xs: 12, md: 4 }}>
                        <FieldLabel
                          label="Plus-Up Distribution"
                          tooltipType="estimated_distribution"
                        />
                        <RpMuiSwitch
                          checked={Boolean(value?.distribution)}
                          onChange={handleDistributionChange}
                        />
                      </Grid>
                      <Grid size={{ xs: 12, md: 8 }}>
                        <FieldLabel
                          label="Estimated Distribution"
                          tooltipType="estimated_distribution"
                        />
                        <ReadonlyDistributionField
                          value={
                            value?.distribution
                              ? Number(value?.estimated_distribution) || 0
                              : 0
                          }
                        />
                      </Grid>
                    </Grid>
                  </Grid>
                  <Grid size={{ xs: 12, md: 4.5 }}>
                    <CostColumn>
                      <FieldLabel
                        label="Estimated Cost"
                        tooltipType="distribution_cost"
                      />
                      <ReadonlyCurrencyField
                        value={value?.plus_up_cost}
                        disabled={!value?.distribution}
                      />
                    </CostColumn>
                  </Grid>
                </Grid>
              </Grid>

              <Grid size={12}>
                <Divider />
              </Grid>
            </>
          ) : null}

          <Grid size={12}>
            <Grid container spacing={3} alignItems="flex-start">
              <Grid size={{ xs: 12, md: 7.5 }}>
                <Grid container spacing={3} alignItems="flex-start">
                  <Grid size={{ xs: 12, md: 4 }}>
                    <FieldLabel
                      label="Add Store Copy"
                      tooltipType="store_copy"
                    />
                    <RpMuiSwitch
                      checked={Boolean(value?.store_copy)}
                      onChange={handleStoreCopyChange}
                    />
                    <Typography
                      variant="caption"
                      display="block"
                      sx={{ mt: 1 }}
                    >
                      Copies will be delivered to: {value?.address || "N/A"}
                    </Typography>
                    <Typography
                      variant="caption"
                      color="text.disabled"
                      display="block"
                      sx={{ mt: 0.5 }}
                    >
                      Not your store's address?{" "}
                      <Link
                        component="button"
                        variant="caption"
                        type="button"
                        onClick={handleOpenFeedback}
                        underline="always"
                        sx={{
                          color: "var(--primary-base)",
                          cursor: "pointer",
                        }}
                      >
                        Click here
                      </Link>
                    </Typography>
                  </Grid>
                  <Grid size={{ xs: 12, md: 8 }}>
                    <FieldLabel label="Store Copy Quantity" />
                    <RpNumberField
                      value={value?.store_copy_qnt || ""}
                      onChange={handleStoreCopyQntChange}
                      size="small"
                      fullWidth
                      disabled={!value?.store_copy || isFixedStoreCopyQuantity}
                      readOnly={isFixedStoreCopyQuantity}
                      placeholder="0"
                      decimalScale={0}
                      maxValue={storeCopyConfig?.maxQuantity}
                      error={Boolean(storeCopyError)}
                      helperText={storeCopyError}
                    />
                  </Grid>
                </Grid>
              </Grid>
              <Grid size={{ xs: 12, md: 4.5 }}>
                <CostColumn>
                  <FieldLabel
                    label="Store Copy Cost"
                    tooltipType="store_copy_cost"
                    cpm={value?.store_copy_cpm}
                  />
                  <ReadonlyCurrencyField
                    value={value?.store_copy_cost}
                    disabled={!value?.store_copy}
                  />
                </CostColumn>
              </Grid>
            </Grid>
          </Grid>

          <Grid size={12}>
            <Divider />
          </Grid>

          <Grid size={{ xs: 12, md: 7.5 }}>
            <Grid container spacing={3} alignItems="flex-start">
              <Grid size={{ xs: 12, md: 4 }} />
              <Grid size={{ xs: 12, md: 8 }}>
                <FieldLabel
                  label="National Mailing Week"
                  tooltipType="mailing_date"
                />
                <ReadonlyTextField
                  value={formatNationalMailingWeek(
                    value?.national_mailing_week,
                    "MMM D, YYYY"
                  )}
                  endIcon={<CalendarTodayOutlinedIcon fontSize="small" />}
                />
              </Grid>
            </Grid>
          </Grid>
          <Grid size={{ xs: 12, md: 4.5 }}>
            <CostColumn>
              <Typography variant="body2" fontWeight={700} sx={{ mb: 1.5 }}>
                Total Cost
              </Typography>
              <ReadonlyCurrencyField
                value={value?.total_cost}
                disabled={!value?.total_cost}
              />
            </CostColumn>
          </Grid>
        </Grid>
      </AccordionDetails>
      <Feedback open={feedbackOpen} handleClose={handleCloseFeedback} />
    </Accordion>
  );
}

const readOnlyInputSx = {
  "& .MuiOutlinedInput-root": {
    opacity: 0.6,
    pointerEvents: "none",
  },
};

export default memo(NationalPlusUpLocationAccordion);

function CostColumn({ children }: { children: ReactNode }) {
  return (
    <Box
      sx={{
        borderLeft: {
          xs: 0,
          md: (theme) => `thin solid ${theme.palette.divider}`,
        },
        pl: { xs: 0, md: 3 },
      }}
    >
      {children}
    </Box>
  );
}

function FieldLabel({
  label,
  tooltipType = "",
  tooltipContent = null,
  cpm = 0,
}: {
  label: string;
  tooltipType?: React.ComponentProps<typeof NationalPlusUpToolTips>["type"];
  tooltipContent?: string | null;
  cpm?: number | string;
}) {
  return (
    <Typography
      variant="body2"
      sx={{ mb: 1.5, display: "flex", alignItems: "center", gap: 0.75 }}
    >
      {label}
      {tooltipType || tooltipContent ? (
        <NationalPlusUpToolTips
          type={tooltipType}
          customContent={tooltipContent}
          cpm={cpm}
        />
      ) : null}
    </Typography>
  );
}

function ReadonlyDistributionField({ value }: { value?: number | string }) {
  return (
    <RpNumberField
      fullWidth
      placeholder="0"
      size="small"
      value={value || 0}
      readOnly
      decimalScale={0}
      sx={readOnlyInputSx}
      slotProps={{
        input: {
          startAdornment: (
            <Icon
              component={RpSvgIcon}
              name="sheltr"
              color="primary"
              className="mr-2 mb-1"
            />
          ),
          readOnly: true,
        },
      }}
    />
  );
}

function ReadonlyCurrencyField({
  value,
  disabled = false,
}: {
  value?: number | string;
  disabled?: boolean;
}) {
  return (
    <RpCurrencyField
      fullWidth
      size="small"
      value={Number(value) || 0}
      disabled={disabled}
      readOnly
      placeholder="0.00"
      fixedDecimalScale={true}
      sx={readOnlyInputSx}
    />
  );
}

function ReadonlyTextField({
  value,
  endIcon,
}: {
  value?: string;
  endIcon?: ReactNode;
}) {
  return (
    <TextField
      value={value || ""}
      size="small"
      fullWidth
      disabled
      InputProps={{
        endAdornment: endIcon,
      }}
      sx={{
        "& .MuiInputBase-root.Mui-disabled": {
          bgcolor: "var(--neutral-200)",
        },
        "& .MuiInputBase-input.Mui-disabled": {
          WebkitTextFillColor: "var(--palette-text-primary)",
        },
      }}
    />
  );
}
